// feedbackBubble — 「タカミン窓口」吹き出し演出 + ドラッグ位置保存の純粋ロジック.
//
// 背景: launch-rollout-plan.md §3.3.1 (= 2026-05-23 W2 公開前投入の前倒し強化).
//
// メンバーリスト等の白背景に溶けて気づかれない問題への対応:
//   1. ランダム吹き出しで「気づかせる」signal を能動的に出す
//   2. ドラッグ移動 + localStorage 位置保存で「邪魔と感じたら避けられる」逃げ道
//   3. 描画中 / モーダル既開 のときは抑制 (= 邪魔しない)
//
// この module は **純粋関数 + 純粋 store のみ**. setTimeout / DOM / Event listener
// は本ファイルでは扱わず、UI 側 (FeedbackButton.tsx) で hook 化する.
// (= application/ 配下 = node env vitest で 100% カバレッジを稼ぐ流儀、
//   bellOnRoomin / feedbackForm と同 pattern.)
//
// MVP スコープ:
//   - 吹き出しメッセージ 3 パターン (A 2 段 / B 3 段 / C 単発)
//   - 発動スケジュール: 初回 30s → 2 分後 → 5-10 分ごとランダム
//   - 連続同パターン回避 (= ランダム選択時 last パターンを除外)
//   - 描画中抑制 / モーダル既開 stop の判定は引数で受ける (= 呼び出し側責務)
//   - 位置 store (localStorage 永続化 + 画面外クランプ)
//
// 後追い:
//   - 「ぴくぴく」アニメ秒数 / カーブのチューニング (= UI 側で実機調整)
//   - パターン文言のローテーション拡張 (= ここに足すだけ)

/**
 * 吹き出しメッセージ 1 セグメント (= 1 つの吹き出し表示).
 * `durationMs` の間表示してから次へ.
 */
export interface BubbleSegment {
  readonly text: string;
  readonly durationMs: number;
}

/**
 * 吹き出しパターン. 1 〜 N セグメントを順番に表示して終わる.
 *
 * パターン ID は連続同パターン回避用 (= last と一致しないものから選ぶ).
 * launch-rollout-plan.md §3.3.1:
 *   A (短、2 段): 「お声を」 → 1.5s → 「お寄せください」 → 2s で消える
 *   B (長、3 段): 「思い出話とかも」 → 1.5s → 「お寄せいただけると」 → 1.5s → 「嬉しいです」 → 2s
 *   C (単発): 「メッセージください」「ご感想ください」「お気軽にどうぞ」
 */
export interface BubblePattern {
  readonly id: string;
  readonly segments: ReadonlyArray<BubbleSegment>;
}

// セグメント間切替時の表示時間 (= 「次の文に進むまで現在を見せる時間」).
// 「吹き出しに気付く → 読む → 切替認識」の "読む" 時間を確保するため
// 3s 採用 (= 当初 1.5s → 2.5s → 3s と 2 度延長、2026-05-22).
const SEG_SHOW_MS = 3_000;
// 最終セグメントの表示時間 (= 余韻 + 「消える前に読み終わる」配慮).
const SEG_TAIL_MS = 4_000;

/**
 * 吹き出しパターン一覧. ランダム選択候補.
 *
 * C は 1 つにまとめず複数 ID にして個別に登録、ランダム時の「単発でも違う文言」
 * バリエーションを増やす (= 連続して同 ID が出ない仕組みで自然な変化を作る).
 */
export const BUBBLE_PATTERNS: ReadonlyArray<BubblePattern> = [
  {
    id: 'A',
    segments: [
      { text: 'お声を', durationMs: SEG_SHOW_MS },
      { text: 'お寄せください', durationMs: SEG_TAIL_MS },
    ],
  },
  {
    id: 'B',
    segments: [
      { text: '思い出話とかも', durationMs: SEG_SHOW_MS },
      { text: 'お寄せいただけると', durationMs: SEG_SHOW_MS },
      { text: '嬉しいです', durationMs: SEG_TAIL_MS },
    ],
  },
  {
    id: 'D',
    segments: [
      { text: 'タカミンへの応援も', durationMs: SEG_SHOW_MS },
      { text: 'お待ちしてます', durationMs: SEG_TAIL_MS },
    ],
  },
  {
    id: 'C1',
    segments: [
      { text: 'メッセージください', durationMs: SEG_SHOW_MS },
      { text: 'お気軽にどうぞ', durationMs: SEG_TAIL_MS },
    ],
  },
  {
    id: 'C2',
    segments: [
      { text: 'ご感想ください', durationMs: SEG_SHOW_MS },
      { text: 'お気軽にどうぞ', durationMs: SEG_TAIL_MS },
    ],
  },
];

/**
 * パターン全体の総表示時間 (= 全 segment の durationMs 合計).
 * UI 側で「吹き出し終了 → 次の発動までの待機」スケジュール計算に使う.
 */
export const totalBubbleDurationMs = (pattern: BubblePattern): number =>
  pattern.segments.reduce((sum, s) => sum + s.durationMs, 0);

/**
 * ランダムにパターンを選ぶ. 直前のパターン ID を除外して連続同パターンを避ける.
 *
 * @param lastId 直前に表示したパターン ID (= 初回は null)
 * @param rng 0..1 の乱数生成器 (テスト時は固定 rng を注入)
 */
export const pickRandomBubblePattern = (
  lastId: string | null,
  rng: () => number = Math.random,
): BubblePattern => {
  const candidates =
    lastId === null ? BUBBLE_PATTERNS : BUBBLE_PATTERNS.filter((p) => p.id !== lastId);
  // 全部除外されるのは想定外だが defensive (= BUBBLE_PATTERNS が 1 つしかない時等).
  const pool = candidates.length > 0 ? candidates : BUBBLE_PATTERNS;
  const idx = Math.floor(rng() * pool.length);
  // floor の浮動小数誤差で範囲外を弾く (= rng() === 1.0 のとき index === length).
  // biome-ignore lint/style/noNonNullAssertion: pool is guaranteed non-empty above.
  return pool[Math.min(idx, pool.length - 1)]!;
};

// ─── 発動スケジュール ────────────────────────────────────────────────────────

/**
 * 発動回数を渡すと「次の発動までの待機 ms」を返す.
 *
 * launch-rollout-plan.md §3.3.1 頻度設計:
 *   初回 (count === 0):  30 秒後
 *   2 回目 (count === 1): 初回から 2 分後
 *   3 回目以降 (count >= 2): 5-10 分ごとランダム
 *
 * @param firedCount 既に発動した回数 (= 0 で「次に出すのは初回」)
 * @param rng 0..1 の乱数生成器
 */
export const nextBubbleDelayMs = (firedCount: number, rng: () => number = Math.random): number => {
  if (firedCount <= 0) return 30_000;
  if (firedCount === 1) return 120_000;
  // 5-10 分 (= 300_000 〜 600_000 ms) でランダム.
  return 300_000 + Math.floor(rng() * 300_001);
};

// ─── 位置 store (localStorage 永続化) ───────────────────────────────────────

/**
 * フローティングボタンの座標 (= 画面左上原点の px). null は「デフォルト位置」.
 */
export interface FeedbackButtonPosition {
  readonly x: number;
  readonly y: number;
}

const POSITION_STORAGE_KEY = 'oekaki:feedback-button-position';

/**
 * localStorage の minimal surface. `globalThis.localStorage` で satisfies.
 * テストは memory storage を注入. (= AnonUidStore / BellOnStorage と同流儀.)
 */
export interface PositionStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const probeBrowserStorage = (): PositionStorage | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = `${POSITION_STORAGE_KEY}__probe__`;
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

/**
 * 永続化された位置の読み書きストア. storage 不可なら read は null 固定、
 * write は silent no-op (= ベル機能と同じ degradation 流儀).
 */
export interface FeedbackButtonPositionStore {
  /** 永続化済みの位置. 不在 / 不正 / storage 不可なら null (= デフォルト). */
  read(): FeedbackButtonPosition | null;
  /** 位置を保存. storage 不可なら silent. */
  write(pos: FeedbackButtonPosition): void;
  /** デフォルト位置 (= 右下) に戻すため永続値を削除. */
  clear(): void;
}

export interface CreatePositionStoreOptions {
  /**
   * Override storage. Default は `globalThis.localStorage` を probe.
   * `null` を渡すと強制 no-storage モード (= read 常に null).
   */
  storage?: PositionStorage | null;
}

const isValidPosition = (raw: unknown): raw is FeedbackButtonPosition => {
  if (typeof raw !== 'object' || raw === null) return false;
  const r = raw as Record<string, unknown>;
  return (
    typeof r.x === 'number' &&
    Number.isFinite(r.x) &&
    typeof r.y === 'number' &&
    Number.isFinite(r.y)
  );
};

export const createFeedbackButtonPositionStore = (
  opts: CreatePositionStoreOptions = {},
): FeedbackButtonPositionStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());

  return {
    read() {
      if (storage === null) return null;
      try {
        const raw = storage.getItem(POSITION_STORAGE_KEY);
        if (raw === null) return null;
        const parsed: unknown = JSON.parse(raw);
        if (!isValidPosition(parsed)) return null;
        return { x: parsed.x, y: parsed.y };
      } catch {
        return null;
      }
    },
    write(pos) {
      if (storage === null) return;
      try {
        storage.setItem(POSITION_STORAGE_KEY, JSON.stringify({ x: pos.x, y: pos.y }));
      } catch {
        // quota / cookie-blocked 等は silent fail.
      }
    },
    clear() {
      if (storage === null) return;
      try {
        storage.removeItem(POSITION_STORAGE_KEY);
      } catch {
        // ignore.
      }
    },
  };
};

// ─── 位置クランプ ───────────────────────────────────────────────────────────

/**
 * 端からの最低余白 (= 画面外に出さないため). §3.3.1 仕様: 16px.
 */
export const POSITION_EDGE_MARGIN = 16;

export interface ClampPositionArgs {
  readonly x: number;
  readonly y: number;
  /** viewport の幅 (= window.innerWidth). */
  readonly viewportWidth: number;
  /** viewport の高さ (= window.innerHeight). */
  readonly viewportHeight: number;
  /** ボタンのサイズ (= 36px 想定、§3.3.1). */
  readonly buttonSize: number;
}

/**
 * 位置を viewport 内 (端から POSITION_EDGE_MARGIN px 余白) に収める.
 *
 * 画面が極端に狭く (= buttonSize + 2*margin > viewport) 当てはまらないケースは
 * margin だけ確保して右下に押し戻す (= 範囲が反転しないよう defensive).
 */
export const clampPosition = (args: ClampPositionArgs): FeedbackButtonPosition => {
  const { x, y, viewportWidth, viewportHeight, buttonSize } = args;
  const minX = POSITION_EDGE_MARGIN;
  const minY = POSITION_EDGE_MARGIN;
  const maxX = Math.max(minX, viewportWidth - buttonSize - POSITION_EDGE_MARGIN);
  const maxY = Math.max(minY, viewportHeight - buttonSize - POSITION_EDGE_MARGIN);
  return {
    x: Math.min(Math.max(x, minX), maxX),
    y: Math.min(Math.max(y, minY), maxY),
  };
};

/**
 * デフォルト位置 = 右下 16px 余白. viewport が読めない / SSR では null を返し、
 * UI 側で CSS デフォルト (= right-4 bottom-4) にフォールバックさせる.
 */
export const computeDefaultPosition = (args: {
  readonly viewportWidth: number;
  readonly viewportHeight: number;
  readonly buttonSize: number;
}): FeedbackButtonPosition => ({
  x: args.viewportWidth - args.buttonSize - POSITION_EDGE_MARGIN,
  y: args.viewportHeight - args.buttonSize - POSITION_EDGE_MARGIN,
});

// ─── ドラッグ判定 ────────────────────────────────────────────────────────────

/**
 * ドラッグと「タップ / クリック」の閾値. 5px 未満の移動はクリック扱い.
 * launch-rollout-plan.md §3.3.1: 「ドラッグ中はクリック扱いしない (= 移動と
 * 開閉を区別、threshold 5px くらい)」.
 */
export const DRAG_CLICK_THRESHOLD_PX = 5;

/**
 * pointer down → up の距離からドラッグかクリックかを判定. 純粋関数.
 *
 * @param dx down からの x 方向移動量 (px)
 * @param dy down からの y 方向移動量 (px)
 */
export const isDragMovement = (dx: number, dy: number): boolean => {
  // 2 乗距離で比較 (= sqrt 不要).
  return dx * dx + dy * dy >= DRAG_CLICK_THRESHOLD_PX * DRAG_CLICK_THRESHOLD_PX;
};

// ─── (旧) モーダル既開フラグの永続化は廃止 (= 2026-05-22 戦略変更) ─────────
//
// 当初仕様: 1 度モーダルを開いたユーザには以降の吹き出しを完全停止
// 変更後  : **吹き出しは永遠に出続ける**
//
// 戦略意図 (= launch-rollout-plan.md §3.3 関連):
//   1. W2 公開時のインパクト最大化 (= 「タカミンの吹き出しずっと出てくる」の話題化)
//   2. 入室者間の会話のタネを能動的に作る
//   3. **「うざいと言われたので×ボタン付けました」自虐ネタを後追い投入** する build-in
//      narrative arc を意図的に作る (= §3.2 毎日ネタ運用 🐛 修正カテゴリの主弾)
//
// 旧 localStorage キー `oekaki:feedback-modal-opened` はリリース前なので
// migration 不要 (= 既存ユーザは存在しない、ユーザー判断 2026-05-22).
